import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import parse from 'html-react-parser';
import { useCallback, useEffect } from 'react';
import _ from 'lodash';
import useSessionStorage from 'usehooks-ts/dist/esm/useSessionStorage/useSessionStorage';
import CustomCard from '../cards/CustomCard';
import {
  categoryIdUrlParam,
  EXPANDED_CATEGORY_IDS,
  settingTypeCategoryRefClickExplanation,
} from '../../shared/constants';
import {
  useGetApiCategoryTreeQuery,
  useGetApiSettingsUserQuery,
} from '../../redux/store/api/api';
import { useAppDispatch } from '../../redux/hooks';
import { setFocusedTreeItem } from '../../redux/store/content/slice';

function CategoryReferenceView() {
  const { t: translation } = useTranslation();
  const dispatch = useAppDispatch();
  const CATEGORY_REFERENCE_BUTTON_ID = 'CategoryReferenceButton';
  const [searchParams] = useSearchParams();
  const categoryId: string | null = searchParams.get(categoryIdUrlParam);
  const { data: contentTreeData, isFetching: categoriesIsFetching } =
    useGetApiCategoryTreeQuery();
  const categoryOfReference = contentTreeData?.resultObject?.categories?.find(
    (c) => c.id === categoryId,
  );
  const { data: settings } = useGetApiSettingsUserQuery();
  const explanatoryText = settings?.resultObject?.find(
    (s) => s.settingType?.key === settingTypeCategoryRefClickExplanation,
  );
  const [
    expandedCategoriesInSessionStorage,
    setExpandedCategoriesInSessionStorage,
  ] = useSessionStorage<string[]>(EXPANDED_CATEGORY_IDS, []);

  const getAllCategoryChildIds = useCallback(
    (cId: string): string[] => {
      const category = contentTreeData?.resultObject?.categories?.find(
        (c) => c.id === cId,
      );

      if (!category?.categoryIds || category.categoryIds.length === 0) {
        return [cId];
      }

      return [
        cId,
        ...category.categoryIds.flatMap((id) => getAllCategoryChildIds(id)),
      ];
    },
    [contentTreeData],
  );

  const openAndScrollToCategoryInTree = useCallback(
    (closeUnrealtesCategories = false, focusOnTreeItem = false) => {
      if (!categoryOfReference || categoriesIsFetching || !contentTreeData) {
        return;
      }

      // Open tree for target category
      const treePath: string[] | undefined = categoryOfReference
        ? [
            '0',
            categoryOfReference.id || '',
            ...(categoryOfReference.treePath || []),
          ]
        : undefined;

      if (treePath && !_.isEqual(treePath, [''])) {
        setExpandedCategoriesInSessionStorage(
          _.union(
            treePath,
            getAllCategoryChildIds(categoryOfReference?.id || ''),
            closeUnrealtesCategories ? [] : expandedCategoriesInSessionStorage,
          ),
        );
      }

      setTimeout(() => {
        const categoryTreeItem: HTMLElement | null = document.getElementById(
          categoryOfReference.id || '',
        );
        const categoryAreaInTree = categoryTreeItem?.parentElement;
        if (categoryTreeItem) {
          categoryTreeItem.scrollIntoView({
            block: 'start',
            behavior: 'instant' as ScrollBehavior,
          });
        }

        if (categoryAreaInTree) {
          categoryAreaInTree.classList.add('opened-category');

          const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            if (target.id !== CATEGORY_REFERENCE_BUTTON_ID) {
              categoryAreaInTree.classList.remove('opened-category');
              document.removeEventListener('click', handleClickOutside);
            }
          };

          setTimeout(() => {
            document.addEventListener('click', handleClickOutside);
          });
        }
      }, 250);

      if (focusOnTreeItem) {
        dispatch(setFocusedTreeItem(categoryId || ''));
      }
    },
    [
      categoryOfReference,
      categoriesIsFetching,
      contentTreeData,
      expandedCategoriesInSessionStorage,
    ],
  );

  useEffect(() => {
    openAndScrollToCategoryInTree(true, false);
  }, []);

  return (
    <CustomCard
      headlineAsH1
      focusableHeadline
      iconClass={categoryOfReference?.categoryTypeIconClass || ''}
      title={categoryOfReference?.name || translation('categoryReference')}
      iconColorHexCode={categoryOfReference?.categoryTypeColorHexCode || ''}
      customCardStyle={{ flex: 1 }}>
      <div className='d-flex flex-column p-4'>
        <p>{parse(explanatoryText?.value || '')}</p>
        <Button
          id={CATEGORY_REFERENCE_BUTTON_ID}
          variant='outline-success'
          className='align-self-start m-auto'
          onClick={() => openAndScrollToCategoryInTree(false, true)}>
          {translation('jumpToNode')}
        </Button>
      </div>
    </CustomCard>
  );
}

export default CategoryReferenceView;
